<template>
    <div>
        <cc-modal :modal="modal" @close="close">
            <div slot="body" class="permission-modal">
                <div class="permission-modal__grid">
                    <section class="permission-modal__panel">
                        <label class="label-control">Perfis</label>
                        <div class="permission-modal__new-profile">
                            <cc-search
                                :placeholder="'Novo Perfil'"
                                :icon="'fa success fa-plus'"
                                :theme="'theme-green'"
                                class="mt-1"
                                v-model="new_profile.name"
                                @search="() => new_profile.name && save(new_profile)"
                            />
                        </div>

                        <div class="sellers-list list-group">
                            <cc-loader v-show="loading"></cc-loader>
                            <table v-show="!loading" class="table">
                                <thead>
                                    <tr>
                                        <th class="text-left px-3">#ID</th>
                                        <th class="text-left px-0">Nome</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="profile in profiles"
                                        :key="'prof-' + profile.id"
                                        :class="{
                                            'permission-modal__profile-row': true,
                                            selected: isSelectedProfile(profile),
                                            'permission-modal__row--readonly': profile.is_base,
                                        }"
                                        @click.stop="select_profile(profile)"
                                    >
                                        <td class="text-left px-3 permission-modal__profile-id">
                                            <span class="permission-modal__id-trigger">
                                                <i class="fas fa-hand-pointer permission-modal__row-link-icon"></i>
                                                <b>#{{ profile.id }}</b>
                                            </span>
                                        </td>
                                        <td class="text-left px-0">
                                            <div class="permission-modal__profile-cell">
                                                <cc-input
                                                    v-model="profile.name"
                                                    :disabled="!isSelectedProfile(profile) || profile.is_base"
                                                />
                                                <small v-if="profile.is_base" class="permission-modal__badge">Base</small>
                                            </div>
                                        </td>
                                        <td class="text-center permission-modal__profile-actions">
                                            <i
                                                class="fa fa-floppy-o remove-btn theme-green"
                                                :class="{ 'permission-modal__action--disabled': profile.is_base }"
                                                @click.stop="save(profile)"
                                            />
                                            <i
                                                v-show="!profile.loading"
                                                class="fa fa-trash remove-btn"
                                                :class="{ 'permission-modal__action--disabled': profile.is_base }"
                                                @click.stop="remove_profile(profile)"
                                            />
                                            <cc-loader v-show="profile.loading" :show_txt="false" class="loader float-right" />
                                        </td>
                                    </tr>
                                    <tr v-if="!loading && profiles.length === 0">
                                        <td colspan="3" class="permission-modal__empty-state">
                                            Nenhum perfil disponível.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section v-if="current_profile.id" class="permission-modal__panel">
                        <label class="label-control">{{ current_profile.name }}</label>
                        <div class="sellers-list list-group">
                            <cc-loader v-show="loading"></cc-loader>
                            <table v-show="!loading" class="table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th class="text-left px-3">Módulo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="module in current_modules"
                                        :key="'perm-res-' + module.resource"
                                        :class="{
                                            selected: isSelectedModule(module),
                                            'permission-modal__row--loading': isPermissionLoading(module),
                                        }"
                                    >
                                        <td class="permission-modal__checkbox-cell" @click.stop>
                                            <span
                                                v-if="isPermissionLoading(module)"
                                                class="permission-modal__spinner"
                                                aria-hidden="true"
                                            />
                                            <div v-else class="checkbox">
                                                <cc-checkbox
                                                    :value="has_permission(module)"
                                                    @input="togglePermission(module, $event)"
                                                />
                                            </div>
                                        </td>
                                        <td class="text-left px-3" @click="select_module(module)">
                                            <div class="permission-modal__module-name">{{ module.name }}</div>
                                            <small class="permission-modal__module-meta">
                                                {{ translateAction(module.action) }}
                                            </small>
                                        </td>
                                    </tr>
                                    <tr v-if="!loading && current_modules.length === 0">
                                        <td colspan="2" class="permission-modal__empty-state">
                                            Nenhuma permissão disponível.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section v-if="current_profile.id" class="permission-modal__panel">
                        <label class="label-control">{{ current_module.name || "Permissões" }}</label>
                        <div class="sellers-list list-group">
                            <cc-loader v-show="loading"></cc-loader>
                            <table v-show="!loading" class="table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th class="text-left px-3">Permissão</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="permission in current_module.permissions"
                                        :key="'perm-' + permissionKey(permission)"
                                        :class="{
                                            selected: has_permission(permission),
                                            'permission-modal__row--loading': isPermissionLoading(permission),
                                        }"
                                        @click="togglePermission(permission)"
                                    >
                                        <td class="permission-modal__status-cell">
                                            <span
                                                v-if="isPermissionLoading(permission)"
                                                class="permission-modal__spinner"
                                                aria-hidden="true"
                                            />
                                            <i
                                                v-else
                                                class="fa fa-check success-update"
                                                :class="{ visible: has_permission(permission) }"
                                            />
                                        </td>
                                        <td class="text-left px-3">
                                            <div class="change-status">
                                                <div class="permission-modal__permission-header">
                                                    <p class="perm-name">
                                                        {{ permission.name || translateAction(permission.action) }}
                                                    </p>
                                                    <span class="permission-modal__permission-action">
                                                        {{ translateAction(permission.action) }}
                                                    </span>
                                                </div>
                                                <small>{{ permission.description || translateAction(permission.action) }}</small>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-if="!loading && current_module.permissions.length === 0">
                                        <td colspan="2" class="permission-modal__empty-state">
                                            Nenhuma permissão disponível para este módulo.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            </div>
        </cc-modal>
    </div>
</template>

<script>
import ErrorHandlerService from "@/services/ErrorHandlerService";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import ProviderProfileService from "@/services/v3/providers/profile.service";
import ClientProfileService from "@/services/v3/clients/profile.service";
import ProfileManagementService from "@/services/v3/management/profile.service";
import PermissionManagementService from "@/services/v3/management/permission.service";
import {
    buildPermissionLookup,
    buildPermissionModules,
    extractCollectionFromPayload,
    extractItemFromPayload,
    filterPermissionsByVisibility,
    getPermissionKey,
    normalizePermissionRecord,
    translatePermissionAction,
} from "@/helpers/permissions";

const EMPTY_MODULE = {
    name: "",
    resource: "",
    action: "",
    permissions: [],
};

export default {
    mixins: [loaderMixin],
    props: {
        id: { type: Number },
        type: { type: String },
        owner_name: { type: String },
        closeDialog: { type: Function },
    },
    data() {
        return {
            profiles: [],
            availablePermissions: [],
            current_profile_id: null,
            current_module_resource: null,
            new_profile: { name: "" },
            loading: false,
            permissionLoadingMap: {},
            svc: new ProviderProfileService(),
            profileManagementSvc: new ProfileManagementService(),
            permissionManagementSvc: new PermissionManagementService(),
            modal: {
                title: "gerenciar perfis",
                subtitle: this.owner_name,
                icon_title: "fas fa-user-lock",
                cancel_text: "Voltar",
                style: {
                    width: "96vw",
                    maxWidth: "1400px",
                },
            },
        };
    },
    computed: {
        permissionVisibility() {
            return this.type === "CLIENT" ? "client" : "provider";
        },
        profileFilters() {
            return Object.assign(
                {
                    visibility: this.permissionVisibility,
                    include_base: true,
                    per_page: "all",
                    with_permissions: true,
                },
                this.type === "CLIENT" ? { client_id: this.id } : { provider_id: this.id }
            );
        },
        current_profile() {
            return (
                this.profiles.find((profile) => String(profile.id) === String(this.current_profile_id)) || {
                    permissions: [],
                }
            );
        },
        current_modules() {
            return buildPermissionModules(filterPermissionsByVisibility(this.availablePermissions, this.permissionVisibility));
        },
        current_module() {
            if (!this.current_modules.length) {
                return EMPTY_MODULE;
            }

            return this.current_modules.find((module) => module.resource === this.current_module_resource) || this.current_modules[0];
        },
        selectedPermissionKeys() {
            return buildPermissionLookup(this.current_profile.permissions || []);
        },
    },
    methods: {
        translateAction(action) {
            return translatePermissionAction(action);
        },
        permissionKey(permission) {
            return getPermissionKey(permission);
        },
        permissionLoadingKey(permission, profileId = this.current_profile.id) {
            return `${profileId || "none"}::${this.permissionKey(permission)}`;
        },
        ownerContext() {
            return this.type === "CLIENT" ? { client_id: this.id } : { provider_id: this.id };
        },
        normalizeProfile(profile) {
            return {
                ...profile,
                permissions: Array.isArray(profile.permissions) ? profile.permissions : [],
            };
        },
        replaceProfile(profile) {
            const normalizedProfile = this.normalizeProfile(profile);
            const profileIndex = this.profiles.findIndex((item) => String(item.id) === String(normalizedProfile.id));

            if (profileIndex === -1) {
                this.profiles.push(normalizedProfile);
            } else {
                this.$set(this.profiles, profileIndex, normalizedProfile);
            }

            this.current_profile_id = normalizedProfile.id;
        },
        setPermissionLoading(permission, isLoading) {
            const key = this.permissionLoadingKey(permission);

            if (isLoading) {
                this.$set(this.permissionLoadingMap, key, true);
                return;
            }

            this.$delete(this.permissionLoadingMap, key);
        },
        ensureCurrentModuleSelection() {
            if (!this.current_modules.length) {
                this.current_module_resource = null;
                return;
            }

            const selectedModuleExists = this.current_modules.some((module) => module.resource === this.current_module_resource);

            if (!selectedModuleExists) {
                this.current_module_resource = this.current_modules[0].resource;
            }
        },
        isSelectedProfile(profile) {
            return String(profile.id) === String(this.current_profile_id);
        },
        isSelectedModule(module) {
            return module.resource === this.current_module.resource;
        },
        isPermissionLoading(permission) {
            return !!this.permissionLoadingMap[this.permissionLoadingKey(permission)];
        },
        has_permission(permission) {
            return this.selectedPermissionKeys.has(this.permissionKey(permission));
        },
        select_profile(profile) {
            this.current_profile_id = profile.id;
            this.ensureCurrentModuleSelection();
        },
        select_module(module) {
            this.current_module_resource = module.resource;
        },
        async loadPermissionCatalog() {
            const response = await this.permissionManagementSvc.list();
            this.availablePermissions = extractCollectionFromPayload(response.data).map((permission) =>
                normalizePermissionRecord(permission)
            );
            this.ensureCurrentModuleSelection();
        },
        async loadProfiles(selectedProfileId = this.current_profile_id) {
            const response = await this.profileManagementSvc.list(this.profileFilters);
            const profiles = extractCollectionFromPayload(response.data).map((profile) => this.normalizeProfile(profile));

            this.profiles = profiles;

            if (selectedProfileId && profiles.some((profile) => String(profile.id) === String(selectedProfileId))) {
                this.current_profile_id = selectedProfileId;
            } else if (!profiles.some((profile) => String(profile.id) === String(this.current_profile_id))) {
                this.current_profile_id = null;
            }

            this.ensureCurrentModuleSelection();
        },
        async load() {
            this.loading = true;

            try {
                await Promise.all([this.loadPermissionCatalog(), this.loadProfiles()]);
            } catch (error) {
                ErrorHandlerService.handle(error, this.$store);
            } finally {
                this.loading = false;
            }
        },
        async reloadProfiles(selectedProfileId = this.current_profile_id) {
            this.loading = true;

            try {
                await this.loadProfiles(selectedProfileId);
            } catch (error) {
                ErrorHandlerService.handle(error, this.$store);
            } finally {
                this.loading = false;
            }
        },
        async save(profile) {
            if (profile.is_base) {
                return;
            }

            this.present_loader("Salvando perfil...");

            try {
                const response = await this.svc.save(Object.assign({}, this.ownerContext(), profile));
                const savedProfile = extractItemFromPayload(response.data);
                const selectedProfileId = (savedProfile && savedProfile.id) || profile.id || this.current_profile_id;

                await this.reloadProfiles(selectedProfileId);
                this.new_profile = { name: "" };
            } catch (error) {
                ErrorHandlerService.handle(error, this.$store);
            } finally {
                this.dismiss_loader();
            }
        },
        async remove_profile(profile) {
            if (profile.is_base) {
                return;
            }

            this.$set(profile, "loading", true);

            try {
                await this.svc.remove(Object.assign({}, this.ownerContext(), profile));
                await this.reloadProfiles(this.isSelectedProfile(profile) ? null : this.current_profile_id);
                this.$store.dispatch("notification/add", {
                    type: "success",
                    message: "Removido com sucesso!",
                });
            } catch (error) {
                ErrorHandlerService.handle(error, this.$store);
            } finally {
                this.$set(profile, "loading", false);
            }
        },
        async save_permission(permission) {
            return this.updateProfilePermission(permission, true);
        },
        async remove_permission(permission) {
            return this.updateProfilePermission(permission, false);
        },
        async updateProfilePermission(permission, enabled) {
            if (!this.current_profile.id || !permission.id || this.isPermissionLoading(permission)) {
                return;
            }

            this.setPermissionLoading(permission, true);

            try {
                const response = await this.profileManagementSvc.togglePermission(this.current_profile.id, permission.id, enabled);
                const updatedProfile = extractItemFromPayload(response.data);

                if (updatedProfile) {
                    this.replaceProfile(updatedProfile);
                }

                this.$store.dispatch("notification/add", {
                    type: "success",
                    message: enabled ? "Permissão salva com sucesso!" : "Removido com sucesso!",
                });
            } catch (error) {
                ErrorHandlerService.handle(error, this.$store);
            } finally {
                this.setPermissionLoading(permission, false);
            }
        },
        togglePermission(permission, enabled) {
            if (this.isPermissionLoading(permission)) {
                return;
            }

            const targetState = typeof enabled === "boolean" ? enabled : !this.has_permission(permission);

            if (targetState === this.has_permission(permission)) {
                return;
            }

            return this.updateProfilePermission(permission, targetState);
        },
        close() {
            this.closeDialog()
        },
    },
    mounted() {
        if (this.type === "CLIENT") {
            this.svc = new ClientProfileService();
        }

        this.load();
    },
};
</script>

<style lang="scss" scoped>
@import "./index.modal";

.permission-modal {
    min-height: min(70vh, 760px);
}

.permission-modal__grid {
    display: grid;
    grid-template-columns: minmax(280px, 340px) minmax(250px, 1fr) minmax(340px, 1.2fr);
    gap: 1.25rem;
    align-items: start;
}

.permission-modal__panel {
    min-width: 0;
}

.permission-modal__new-profile {
    margin-bottom: 1rem;
}

.permission-modal__profile-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.permission-modal__profile-actions {
    white-space: nowrap;
}

.permission-modal__profile-row,
.permission-modal__profile-id,
.permission-modal__id-trigger {
    cursor: pointer;
}

.permission-modal__id-trigger {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.permission-modal__row-link-icon {
    color: #9aa5b5;
    font-size: 0.9rem;
}

.permission-modal__profile-row.selected .permission-modal__row-link-icon {
    color: #355ca8;
}

.permission-modal__checkbox-cell,
.permission-modal__status-cell {
    width: 3rem;
}

.permission-modal__row--loading {
    opacity: 0.82;
    background: linear-gradient(90deg, rgba(53, 92, 168, 0.04), rgba(53, 92, 168, 0.1), rgba(53, 92, 168, 0.04));
    background-size: 200% 100%;
    animation: permission-modal-row-loading 1.1s ease-in-out infinite;
}

.permission-modal__spinner {
    display: inline-block;
    width: 1rem;
    height: 1rem;
    border: 2px solid #dbe4f5;
    border-top-color: #355ca8;
    border-radius: 50%;
    animation: permission-modal-spin 0.7s linear infinite;
}

.permission-modal__module-name,
.permission-modal__permission-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.permission-modal__module-meta,
.permission-modal__permission-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.15rem 0.55rem;
    border-radius: 999px;
    background: #eef3ff;
    color: #355ca8;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    white-space: nowrap;
}

.permission-modal__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.15rem 0.55rem;
    border-radius: 999px;
    background: #edf5ff;
    color: #1b5fb8;
    font-size: 0.72rem;
    font-weight: 700;
    white-space: nowrap;
}

.permission-modal__action--disabled {
    opacity: 0.35;
    pointer-events: none;
}

.permission-modal__row--readonly {
    background: #f8fafc;
}

.permission-modal__empty-state {
    color: #6e6e6e;
    text-align: center;
}

.change-status {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
}

.perm-name {
    margin: 0;
    font-weight: 600;
}

::v-deep .modal-body {
    max-height: none !important;
}

::v-deep .modal-container {
    padding: 20px 32px;
}

::v-deep .sellers-list {
    height: clamp(24rem, 58vh, 36rem);
    border: 1px solid #ececec;
    border-radius: 12px;
    overflow: auto;
}

::v-deep .sellers-list .table {
    margin-bottom: 0;
}

::v-deep .sellers-list .table th {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 1;
}

::v-deep .sellers-list .selected {
    background: #f5f8ff;
}

@keyframes permission-modal-spin {
    to {
        transform: rotate(360deg);
    }
}

@keyframes permission-modal-row-loading {
    0% {
        background-position: 200% 0;
    }

    100% {
        background-position: -200% 0;
    }
}

@media only screen and (max-width: 1100px) {
    .permission-modal__grid {
        grid-template-columns: 1fr;
    }

    .permission-modal {
        min-height: auto;
    }
}

@media only screen and (max-width: 768px) {
    ::v-deep .modal-container {
        width: 96vw !important;
        padding: 20px;
    }
}
</style>
